import { BadgeDollarSign, CircleStop, Gavel, LockKeyhole, RefreshCw, Sparkles } from 'lucide-react';
import { useState } from 'react';
import { formatMoney } from '../utils/board.js';
import { Dice } from './Dice.jsx';

export function GameControls({ game, viewerId, action }) {
  const [busy, setBusy] = useState(null);
  const me = game.players.find((player) => player.id === viewerId);
  const current = game.currentPlayer;
  const isMyTurn = game.currentPlayerId === viewerId;
  const pendingProperty = game.pendingPurchase ? game.board[game.pendingPurchase.propertyId] : null;
  const myDebt = game.pendingDebt?.debtorId === viewerId ? game.pendingDebt : null;

  const perform = async (event, payload, key = event) => {
    setBusy(key);
    try {
      await action(event, payload);
    } catch {
      // Сообщение сервера показывается глобально.
    } finally {
      setBusy(null);
    }
  };

  if (game.status === 'finished') {
    return (
      <section className="panel turn-panel turn-panel--winner">
        <Sparkles size={28} />
        <span>Победитель</span>
        <strong>{game.winner?.name}</strong>
        <p>Последний действующий игрок сохранил свою империю.</p>
      </section>
    );
  }

  return (
    <section className={`panel turn-panel ${isMyTurn || myDebt ? 'is-my-turn' : ''}`} aria-labelledby="turn-heading">
      <header className="turn-panel__heading">
        <div>
          <span>{myDebt ? 'Ваш обязательный платёж' : isMyTurn ? 'Ваш ход' : 'Сейчас ходит'}</span>
          <h2 id="turn-heading">{isMyTurn || myDebt ? me?.name : current?.name}</h2>
          {me && <strong className="turn-balance">Баланс: {formatMoney(me.money)}</strong>}
        </div>
        <span className="phase-label">{
          game.phase === 'awaiting_purchase' ? 'Решение о покупке'
            : game.phase === 'awaiting_debt' ? 'Погашение долга'
              : game.phase === 'awaiting_end' ? 'Ход завершён'
                : 'Бросок кубиков'
        }</span>
      </header>

      <Dice key={game.dice.sequence} dice={game.dice} />

      {!isMyTurn && !myDebt && <p className="turn-hint">{current?.isBot ? 'Бот играет автоматически · вы наблюдаете за ходом.' : 'Изменения появятся здесь сразу после действия игрока.'}</p>}

      {isMyTurn && me?.jail.inJail && game.phase === 'awaiting_roll' && (
        <div className="jail-actions">
          <p><LockKeyhole size={16} /> Вы в тюрьме. Выбросьте дубль или освободитесь заранее.</p>
          <div>
            <button className="button button--secondary" onClick={() => perform('jail:pay', {}, 'jail-pay')} disabled={Boolean(busy) || me.money < game.settings.jailFine}>Заплатить {game.settings.jailFine}</button>
            {me.jailFreeCards > 0 && <button className="button button--secondary" onClick={() => perform('jail:use-card', {}, 'jail-card')} disabled={Boolean(busy)}>Карточка ({me.jailFreeCards})</button>}
          </div>
        </div>
      )}

      {isMyTurn && game.phase === 'awaiting_roll' && (
        <button className="button button--primary button--wide button--roll" type="button" onClick={() => perform('game:roll', {}, 'roll')} disabled={Boolean(busy)}>
          <RefreshCw className={busy === 'roll' ? 'spin-fast' : ''} size={20} />
          {busy === 'roll' ? 'Кубики летят…' : 'Бросить кубики'}
        </button>
      )}

      {isMyTurn && game.phase === 'awaiting_purchase' && game.pendingPurchase?.playerId === viewerId && (
        <div className="purchase-decision">
          <div><BadgeDollarSign size={20} /><span>Купить «{pendingProperty.name}»?</span><strong>{formatMoney(pendingProperty.price)}</strong></div>
          <div>
            <button className="button button--primary" type="button" onClick={() => perform('property:buy', {}, 'buy')} disabled={Boolean(busy) || me.money < pendingProperty.price}>Купить</button>
            <button className="button button--secondary" type="button" onClick={() => perform('property:decline', {}, 'decline')} disabled={Boolean(busy)}>Отказаться</button>
          </div>
        </div>
      )}

      {myDebt && (
        <div className="debt-decision">
          <p><Gavel size={18} /> Обязательный платёж: <strong>{formatMoney(myDebt.amount)}</strong></p>
          <span>{myDebt.reason}. Баланс: {formatMoney(me.money)}. Продайте постройки или заложите объекты.</span>
          <div>
            <button className="button button--primary" type="button" onClick={() => perform('debt:pay', {}, 'debt')} disabled={Boolean(busy) || me.money < myDebt.amount}>Погасить долг</button>
            <button className="button button--danger" type="button" onClick={() => perform('bankruptcy:declare', {}, 'bankruptcy')} disabled={Boolean(busy)}><CircleStop size={17} /> Банкротство</button>
          </div>
        </div>
      )}

      {isMyTurn && game.phase === 'awaiting_end' && (
        <button className="button button--primary button--wide" type="button" onClick={() => perform('game:end-turn', {}, 'end')} disabled={Boolean(busy)}>
          Завершить ход
        </button>
      )}
    </section>
  );
}
